import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export const LANGUAGES = ['en', 'ar'] as const;
export type Language = (typeof LANGUAGES)[number];

const en = {
  brand: 'SL Meetings',
  nav: { meetings: 'Meetings', new: 'New meeting', upload: 'Upload', vocabulary: 'Vocabulary' },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    loading: 'Loading…',
    error: 'Something went wrong',
    retry: 'Retry',
    signOut: 'Sign out',
    none: '—',
    language: 'العربية',
    optional: 'optional',
  },
  login: {
    title: 'Sign in',
    subtitle: 'Shahira Lasheen — meeting records',
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    failed: 'Wrong email or password',
  },
  meetings: {
    title: 'Meetings',
    empty: 'No meetings yet.',
    search: 'Search title, client, location',
    filterType: 'Type',
    filterStatus: 'Status',
    from: 'From',
    to: 'To',
    all: 'All',
    duration: 'Duration',
    created: 'Created by',
  },
  types: {
    CLIENT: 'Client',
    SUPPLIER: 'Supplier',
    INTERNAL: 'Internal',
    MANAGEMENT: 'Management',
    ONLINE: 'Online',
    GENERIC: 'General',
  },
  statuses: {
    RECORDING: 'Recording',
    UPLOADED: 'Uploaded',
    TRANSCRIBING: 'Transcribing',
    DRAFTING: 'Drafting',
    IN_REVIEW: 'In review',
    FINAL: 'Final',
    ARCHIVED: 'Archived',
    FAILED: 'Failed',
    NO_SPEECH: 'No speech detected',
  },
  newMeeting: {
    title: 'New meeting',
    meetingTitle: 'Title',
    type: 'Meeting type',
    department: 'Department',
    clientRef: 'Client reference',
    leadRef: 'Lead reference',
    location: 'Location',
    language: 'Meeting language',
    attendees: 'Attendees',
    addAttendee: 'Add attendee',
    attendeeName: 'Name',
    attendeeRole: 'Role in meeting',
    create: 'Create and record',
    createUpload: 'Create and upload a file',
  },
  recorder: {
    title: 'Recorder',
    start: 'Start recording',
    stop: 'Stop and process',
    live: 'Recording',
    idle: 'Ready',
    uploaded: 'Chunks uploaded',
    pending: 'Waiting to upload',
    failed: 'Failed chunks',
    retrying: 'Still retrying',
    stalled: 'A chunk is taking a long time to upload. It is still being retried — keep this page open.',
    captureLost: 'The microphone stopped sending audio. Check the device, then stop and finish.',
    recorderError: 'The recorder stopped unexpectedly.',
    stopFailed: 'Not finished',
    stopFailedHelp:
      'The recording is safe on the server. Finish the upload again — do not start a new recording.',
    retryFinish: 'Retry finishing',
    resume: 'Resume upload',
    resumeAvailable: 'An unfinished recording with {{count}} chunk(s) is stored on this device.',
    offline: 'Offline — chunks are queued and will upload when the connection returns.',
    micDenied: 'Microphone permission was refused.',
    unsupported: 'This browser cannot record audio.',
    wakeLock: 'Screen kept awake',
    finishing: 'Finishing upload…',
    uploadingPiece: 'Uploading piece {{current}} of {{total}}',
    lastError: 'Last error',
    done: 'Recording handed to processing.',
    keepOpen: 'Keep this page open until the upload finishes.',
    micCheck: 'Mic check',
    micLabel: 'Mic: {{label}}',
    micLabelUnknown: 'Phone microphone',
    micLevelAria: 'Microphone input level',
    startMicCheck: 'Start mic check',
    micUnavailable:
      'The microphone could not be opened. Allow microphone access for this site in the browser settings, make sure no other app is using it, then reload this page.',
    selectMic: 'Microphone',
    silenceWarning:
      'No sound is reaching the microphone. Check that the phone is not connected to another device as a microphone, and that nothing else is using the mic.',
  },
  upload: {
    title: 'Upload a recording',
    pick: 'Choose an audio or video file',
    drop: 'Drop a file here, or tap to choose',
    submit: 'Upload',
    uploading: 'Uploading',
    done: 'Uploaded. Transcription has been queued.',
    tooBig: 'That file is larger than 500 MB.',
  },
  meeting: {
    transcript: 'Transcript',
    noTranscript: 'No transcript yet.',
    provider: 'Provider',
    cost: 'Estimated cost',
    mapSpeakers: 'Name speakers',
    retranscribe: 'Re-transcribe',
    retranscribing: 'Queued for re-transcription.',
    edit: 'Edit',
    speakerDialog: 'Map speaker labels to names',
    processing: 'This meeting is still being processed. The page refreshes automatically.',
    failed: 'Processing failed',
    audioMissing: 'Audio is not available yet.',
    diagnostics: 'Upload diagnostics',
    diagnosticsEmpty: 'No client diagnostics for this meeting.',
    chunks: 'Uploaded pieces',
    chunksEmpty: 'No chunk records for this meeting.',
    chunkIndex: 'Piece',
    chunkAttempt: 'Attempt',
    chunkSize: 'Size',
    chunkUploaded: 'Uploaded',
    build: 'Build',
  },
  vocabulary: {
    title: 'Vocabulary',
    subtitle: 'Names and house terms sent to the transcription provider as key terms.',
    term: 'Term',
    category: 'Category',
    aliases: 'Aliases (comma separated)',
    add: 'Add term',
    remove: 'Remove',
    adminOnly: 'Only an administrator can change the vocabulary.',
  },
  categories: {
    CLIENT: 'Client',
    STAFF: 'Staff',
    MATERIAL: 'Material',
    TECHNIQUE: 'Technique',
    PRODUCT: 'Product',
    OTHER: 'Other',
  },
};

const ar: typeof en = {
  brand: 'اجتماعات SL',
  nav: { meetings: 'الاجتماعات', new: 'اجتماع جديد', upload: 'رفع تسجيل', vocabulary: 'المصطلحات' },
  common: {
    save: 'حفظ',
    cancel: 'إلغاء',
    close: 'إغلاق',
    loading: 'جارٍ التحميل…',
    error: 'حدث خطأ',
    retry: 'إعادة المحاولة',
    signOut: 'تسجيل الخروج',
    none: '—',
    language: 'English',
    optional: 'اختياري',
  },
  login: {
    title: 'تسجيل الدخول',
    subtitle: 'شهيرة لاشين — سجل الاجتماعات',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    submit: 'دخول',
    failed: 'البريد الإلكتروني أو كلمة المرور غير صحيحة',
  },
  meetings: {
    title: 'الاجتماعات',
    empty: 'لا توجد اجتماعات بعد.',
    search: 'ابحث في العنوان أو العميل أو المكان',
    filterType: 'النوع',
    filterStatus: 'الحالة',
    from: 'من',
    to: 'إلى',
    all: 'الكل',
    duration: 'المدة',
    created: 'أنشأه',
  },
  types: {
    CLIENT: 'عميل',
    SUPPLIER: 'مورّد',
    INTERNAL: 'داخلي',
    MANAGEMENT: 'إدارة',
    ONLINE: 'أونلاين',
    GENERIC: 'عام',
  },
  statuses: {
    RECORDING: 'جارٍ التسجيل',
    UPLOADED: 'تم الرفع',
    TRANSCRIBING: 'جارٍ التفريغ',
    DRAFTING: 'إعداد المسودة',
    IN_REVIEW: 'قيد المراجعة',
    FINAL: 'نهائي',
    ARCHIVED: 'مؤرشف',
    FAILED: 'فشل',
    NO_SPEECH: 'لا يوجد كلام',
  },
  newMeeting: {
    title: 'اجتماع جديد',
    meetingTitle: 'العنوان',
    type: 'نوع الاجتماع',
    department: 'القسم',
    clientRef: 'مرجع العميل',
    leadRef: 'مرجع العميل المحتمل',
    location: 'المكان',
    language: 'لغة الاجتماع',
    attendees: 'الحضور',
    addAttendee: 'إضافة حاضر',
    attendeeName: 'الاسم',
    attendeeRole: 'الصفة في الاجتماع',
    create: 'إنشاء وبدء التسجيل',
    createUpload: 'إنشاء ورفع ملف',
  },
  recorder: {
    title: 'التسجيل',
    start: 'ابدأ التسجيل',
    stop: 'إيقاف ومعالجة',
    live: 'جارٍ التسجيل',
    idle: 'جاهز',
    uploaded: 'المقاطع المرفوعة',
    pending: 'في انتظار الرفع',
    failed: 'مقاطع فشل رفعها',
    retrying: 'ما زالت المحاولة جارية',
    stalled: 'رفع أحد المقاطع يستغرق وقتاً طويلاً. المحاولة مستمرة — اترك الصفحة مفتوحة.',
    captureLost: 'توقف الميكروفون عن إرسال الصوت. تحقق من الجهاز ثم أوقف التسجيل وأنهِ الرفع.',
    recorderError: 'توقف المسجل بشكل غير متوقع.',
    stopFailed: 'لم يكتمل',
    stopFailedHelp: 'التسجيل محفوظ على الخادم. أعد إنهاء الرفع — لا تبدأ تسجيلاً جديداً.',
    retryFinish: 'إعادة إنهاء الرفع',
    resume: 'استئناف الرفع',
    resumeAvailable: 'يوجد تسجيل غير مكتمل ({{count}} مقطع) محفوظ على هذا الجهاز.',
    offline: 'لا يوجد اتصال — المقاطع محفوظة وسترفع عند عودة الاتصال.',
    micDenied: 'تم رفض إذن الميكروفون.',
    unsupported: 'هذا المتصفح لا يدعم التسجيل الصوتي.',
    wakeLock: 'الشاشة مفعّلة',
    finishing: 'جارٍ إنهاء الرفع…',
    uploadingPiece: 'جارٍ رفع المقطع {{current}} من {{total}}',
    lastError: 'آخر خطأ',
    done: 'تم تسليم التسجيل للمعالجة.',
    keepOpen: 'اترك هذه الصفحة مفتوحة حتى ينتهي الرفع.',
    micCheck: 'فحص الميكروفون',
    micLabel: 'الميكروفون: {{label}}',
    micLabelUnknown: 'ميكروفون الهاتف',
    micLevelAria: 'مستوى دخل الميكروفون',
    startMicCheck: 'بدء فحص الميكروفون',
    micUnavailable:
      'تعذّر فتح الميكروفون. اسمح لهذا الموقع باستخدام الميكروفون من إعدادات المتصفح، وتأكد من عدم استخدامه في تطبيق آخر، ثم أعد تحميل الصفحة.',
    selectMic: 'الميكروفون',
    silenceWarning:
      'لا يصل أي صوت إلى الميكروفون. تأكد من أن الهاتف غير متصل كميكروفون بجهاز آخر، وأنه لا يوجد تطبيق آخر يستخدم الميكروفون.',
  },
  upload: {
    title: 'رفع تسجيل',
    pick: 'اختر ملف صوت أو فيديو',
    drop: 'أفلت الملف هنا أو اضغط للاختيار',
    submit: 'رفع',
    uploading: 'جارٍ الرفع',
    done: 'تم الرفع. تم إدراج التفريغ في قائمة المعالجة.',
    tooBig: 'حجم الملف أكبر من ٥٠٠ ميجابايت.',
  },
  meeting: {
    transcript: 'النص',
    noTranscript: 'لا يوجد نص بعد.',
    provider: 'المزوّد',
    cost: 'التكلفة التقديرية',
    mapSpeakers: 'تسمية المتحدثين',
    retranscribe: 'إعادة التفريغ',
    retranscribing: 'تم إدراج إعادة التفريغ.',
    edit: 'تعديل',
    speakerDialog: 'اربط تسميات المتحدثين بالأسماء',
    processing: 'الاجتماع قيد المعالجة. تُحدَّث الصفحة تلقائياً.',
    failed: 'فشلت المعالجة',
    audioMissing: 'الصوت غير متاح بعد.',
    diagnostics: 'تشخيص الرفع',
    diagnosticsEmpty: 'لا توجد سجلات تشخيص لهذا الاجتماع.',
    chunks: 'المقاطع المرفوعة',
    chunksEmpty: 'لا توجد سجلات مقاطع لهذا الاجتماع.',
    chunkIndex: 'المقطع',
    chunkAttempt: 'المحاولة',
    chunkSize: 'الحجم',
    chunkUploaded: 'وقت الرفع',
    build: 'الإصدار',
  },
  vocabulary: {
    title: 'المصطلحات',
    subtitle: 'الأسماء ومصطلحات الدار التي تُرسل لمزوّد التفريغ كمفردات مفتاحية.',
    term: 'المصطلح',
    category: 'التصنيف',
    aliases: 'مرادفات (مفصولة بفواصل)',
    add: 'إضافة مصطلح',
    remove: 'حذف',
    adminOnly: 'التعديل متاح للمسؤول فقط.',
  },
  categories: {
    CLIENT: 'عميل',
    STAFF: 'فريق',
    MATERIAL: 'خامة',
    TECHNIQUE: 'تقنية',
    PRODUCT: 'منتج',
    OTHER: 'أخرى',
  },
};

const STORAGE_KEY = 'sl-meetings-lang';

export function storedLanguage(): Language {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'ar' || value === 'en') return value;
  } catch {
    /* private mode */
  }
  return 'en';
}

export function applyDirection(lang: Language): void {
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* private mode */
  }
}

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en }, ar: { translation: ar } },
  lng: storedLanguage(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
});

applyDirection(storedLanguage());

export default i18n;
